import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { action, observable, computed } from 'mobx';
import { withTheme } from 'styled-components';
import Translate, { translate } from '../Translate';
import ClickLayer from '../ClickLayer';
import Icon from '../Icon';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import InlineDisclaimers from '../InlineDisclaimers';
import { ChildReferences } from '../../utils/accessibility/ChildReferences';
import { KeyboardEvents } from '../../utils/accessibility/KeyboardEvents';
import { Popper } from '../../utils/Popper';
import { Modifier, OptionsGeneric } from '@popperjs/core';
import { KEYBOARD_KEYS, KEYBOARD_STATE_KEY } from '../../constants';

import DropdownButton from './Components/DropdownButton';

import {
    DropdownDisclaimerWrapper,
    DropdownInnerWrapper,
    DropdownText,
    DropdownWrapper,
} from './StyledComponents';

import { DropdownItem } from '../../models/components/Dropdown/DropdownItem';
import { DropdownProps, DropdownThemeKeys } from './Models';
import DropdownMenu from './Components/DropdownMenu';
import { createPortal } from 'react-dom';
import { SuperscriptScope } from '../../stores';

@inject('accessibilityStore', 'dataStore')
@observer
class Dropdown extends Popper<DropdownProps, {}, DropdownThemeKeys> {
    @observable.ref isOpen: boolean = false;
    @observable.ref showDropdown: boolean = false;
    @observable.ref rawSelectedItemIndex: number = -1;
    @observable.ref rawFocusedIndex: number = -1;
    @observable.ref focusHeader: boolean | undefined;
    keyboardEvents: KeyboardEvents<HTMLButtonElement>;

    DEFAULT_THEME_KEYS: DropdownThemeKeys = {
        button: 'DropdownButton',
        buttonIcon: 'DropdownChevron',
        buttonLabel: 'DropdownText',
        root: 'DropdownWrapper',
        list: 'DropdownList',
        listItem: 'DropdownListItem',
        listWrapper: 'DropdownMenu',
        wrapper: 'DropdownInnerWrapper',
        disclaimerWrapper: 'DropdownDisclaimerWrapper',
    };

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = this
        .props?.popperOptions
        ? this.props.popperOptions
        : {
              placement: 'bottom-end',
              modifiers: [
                  {
                      name: 'offset',
                      options: {
                          offset: [0, 1],
                      },
                  },
                  {
                      name: 'flip',
                      enabled: false,
                  },
              ],
          };

    @computed
    get popperEnabled(): boolean {
        return this.isOpen;
    }

    @computed
    get isHeaderFocused(): boolean {
        const isFocused = this.focusHeader === true;
        return isFocused;
    }

    set isHeaderFocused(state: boolean) {
        this.focusHeader = state;
    }

    @computed
    get focusedIndex(): number {
        return this.rawFocusedIndex < 0 && this.selectedItemIndex >= 0
            ? this.selectedItemIndex
            : this.rawFocusedIndex;
    }

    set focusedIndex(index: number) {
        this.rawFocusedIndex = index;
    }

    @computed
    get selectedItemIndex(): number {
        const { items, selectedItem } = this.props;
        let index = this.rawSelectedItemIndex;
        if (this.rawSelectedItemIndex < 0 && selectedItem !== undefined) {
            index = items.findIndex(item => item.id === selectedItem.id);
        }
        return index;
    }

    set selectedItemIndex(index: number) {
        this.rawSelectedItemIndex = index;
    }

    @computed
    get selectedItem() {
        return (
            this.props.selectedItem || this.props.items[this.selectedItemIndex]
        );
    }

    constructor(props) {
        super(props);
        this.initializeKeyboardEvents();
    }

    @computed
    get items(): Array<DropdownItem> {
        const { items } = this.props;
        return items;
    }

    @computed
    get childReferences(): ChildReferences<HTMLLIElement> {
        return new ChildReferences({
            name: 'Dropdown',
            childCount: this.items.length,
            debug: false,
        });
    }

    @action
    initializeKeyboardEvents() {
        const { pageKeyTraverseSize = 3 } = this.props;
        this.keyboardEvents = new KeyboardEvents({
            name: 'Dropdown',
            debug: false,
            defaults: {
                navigation: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
                invalid: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                    up: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
            },
        });

        // KEY EVENTS
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.DOWN_ARROW,
            'down',
            () => {
                if (this.isOpen) {
                    this.focusedIndex =
                        (this.focusedIndex + 1) % this.items.length;
                } else {
                    this.openDropdown();
                }
            }
        );
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.UP_ARROW, 'down', () => {
            if (this.isOpen) {
                this.focusedIndex =
                    this.focusedIndex === 0
                        ? this.items.length - 1
                        : this.focusedIndex - 1;
            } else {
                this.openDropdown();
            }
        });
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.PAGE_DOWN,
            'down',
            () => {
                if (this.isOpen) {
                    if (this.focusedIndex === this.items.length - 1) {
                        this.focusedIndex = 0;
                    } else if (
                        this.focusedIndex + pageKeyTraverseSize >
                        this.items.length - 1
                    ) {
                        this.focusedIndex = this.items.length - 1;
                    } else {
                        this.focusedIndex =
                            (this.focusedIndex + pageKeyTraverseSize) %
                            this.items.length;
                    }
                }
            }
        );
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.PAGE_UP, 'down', () => {
            if (this.isOpen) {
                if (
                    this.focusedIndex - pageKeyTraverseSize < 0 &&
                    this.focusedIndex !== 0
                ) {
                    this.focusedIndex = 0;
                } else if (this.focusedIndex - pageKeyTraverseSize < 0) {
                    this.focusedIndex = this.items.length - 1;
                } else {
                    this.focusedIndex = this.focusedIndex - pageKeyTraverseSize;
                }
            }
        });
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.HOME, 'down', () => {
            if (this.isOpen) {
                this.focusedIndex = 0;
            }
        });
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.END, 'down', () => {
            if (this.isOpen) {
                this.focusedIndex = this.items.length - 1;
            }
        });

        // STATE EVENTS
        this.keyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.POST_NAVIGATION,
            'down',
            e => {
                this.childReferences.focusChild(this.focusedIndex);
            }
        );
        this.keyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.ACCEPT,
            'down',
            null,
            {
                preventDefault: true,
                stopPropagation: true,
            }
        );
        this.keyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.ACCEPT,
            'up',
            e => {
                e.preventDefault();
                if (!this.isOpen) {
                    this.openDropdown();
                } else if (
                    this.isOpen &&
                    this.items.length > 0 &&
                    this.focusedIndex >= 0
                ) {
                    const item = this.items[this.focusedIndex];
                    this.handleOnClick(e, item, this.focusedIndex);
                }
            }
        );

        // MIXED
        this.keyboardEvents.bindEventToMany(
            [
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.TAB },
                { type: 'key', direction: 'up', key: KEYBOARD_KEYS.ESCAPE },
            ],
            e => {
                this.closeDropdown();
            }
        );
    }

    @action
    toggleOpen = event => {
        event.stopPropagation();
        if (this.isOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    };

    @action
    closeDropdown = () => {
        this.isOpen = false;
        this.isHeaderFocused = true;
        this.focusedIndex = -1;
    };

    @action
    openDropdown = () => {
        this.isOpen = true;
        this.isHeaderFocused = false;
    };

    @action
    setSelectedItem = (item: DropdownItem, index: number) => {
        this.selectedItemIndex = index;
        this.props.selectItemFn(item);
        this.closeDropdown();
    };

    @action
    clickNestedComponent = (index: number) => {
        const childRef = this.childReferences.childReferences[index];
        (childRef.firstElementChild as HTMLElement)?.click();
    };

    @action
    handleOnClick = (
        event: React.SyntheticEvent<HTMLElement>,
        item: DropdownItem,
        index: number
    ) => {
        event.stopPropagation();
        // Dropdown items may contain a component, in which case selecting an item should click the nested component
        if (item.component) {
            this.clickNestedComponent(index);
        } else {
            this.setSelectedItem(item, index);
        }
    };

    @action
    setDropdownMenuElement = (element: HTMLElement) => {
        this.setPopupNodeRef(element);
        // Show the visually hidden Dropdown after Popper initialization, this ensures correct positioning
        if (element) {
            this.showDropdown = true;
            return;
        }
        this.showDropdown = false;
    };

    @computed
    get buttonKeyboardProps() {
        return {
            onKeyDown: this.keyboardEvents.handleKeyboardEventsFor('down'),
        };
    }

    getItemLabel(item: DropdownItem): string {
        if (item) {
            const { itemLabelFn } = this.props;
            if (itemLabelFn) {
                return itemLabelFn(item);
            }
            return item.label;
        }
        return;
    }

    render() {
        const {
            props: {
                children,
                selectItemFn: selectItem,
                itemLabelFn,
                hideLabel = false,
                items,
                placeHolder,
                chevronIcon,
                chevronIconExpanded,
                accessibilityStore,
                dataStore,
                buttonAccessibilityKey,
                themeKeys,
                theme,
                superscriptScope = SuperscriptScope.module,
                ...props
            },
            isOpen,
            selectedItemIndex,
            toggleOpen,
            selectedItem,
            handleOnClick,
            setDropdownMenuElement,
            keyboardEvents,
            showDropdown,
            childReferences,
        } = this;

        if (!dataStore || !dataStore.shadowRoot) {
            return null;
        }

        const selectedLabel = this.getItemLabel(selectedItem) || placeHolder;

        const translatedSelectedLabel =
            (translate &&
                translate(dataStore.stores.translation, selectedLabel)) ||
            selectedLabel;

        const dropdownElement = dataStore.shadowRoot.getElementById(
            'fundcenter-dropdown'
        );

        return (
            <DropdownWrapper {...props} {...this.getTheme('root')}>
                <DropdownInnerWrapper
                    ref={this.setAnchorNodeRef}
                    {...this.getTheme('wrapper')}
                >
                    <DropdownButton
                        title={accessibilityStore.buttonAccessibilityValue(
                            buttonAccessibilityKey || 'DropdownButton'
                        )}
                        onClick={toggleOpen}
                        dataSelected={isOpen}
                        dataMetricId={`moduleDropdown`}
                        isFocused={this.isHeaderFocused}
                        {...this.buttonKeyboardProps}
                        {...this.getTheme('button')}
                    >
                        {!hideLabel && (
                            <DropdownText {...this.getTheme('buttonLabel')}>
                                <Translate>
                                    {selectedLabel
                                        ? selectedLabel
                                        : this.props.placeHolder}
                                </Translate>
                                <DropdownDisclaimerWrapper
                                    {...this.getTheme('disclaimerWrapper')}
                                >
                                    <InlineDisclaimers
                                        conditionalDisclaimers={
                                            selectedItem?.disclaimers
                                        }
                                        superscriptScope={superscriptScope}
                                        metricContext={`dropDown-${camelCase(
                                            selectedLabel
                                        )}`}
                                        forceSuperScripts={dataStore.isMobile}
                                        accessibilityContext={`for disclaimers of the selected dropdown item ${translatedSelectedLabel}`}
                                    />
                                </DropdownDisclaimerWrapper>
                            </DropdownText>
                        )}
                        <Icon
                            fileName={
                                isOpen
                                    ? chevronIconExpanded ||
                                      'DropdownChevronIconExpanded'
                                    : chevronIcon || 'DropdownChevronIcon'
                            }
                            aria-hidden={true}
                            {...this.getTheme('buttonIcon')}
                        />
                    </DropdownButton>
                    {children}
                    {isOpen && (
                        <Fragment>
                            <ClickLayer
                                data-metric-id={'moduleDropdown-clickLayer'}
                                onClick={toggleOpen}
                            />
                            {createPortal(
                                <DropdownMenu
                                    items={items}
                                    handleOnClick={handleOnClick}
                                    selectedItemIndex={selectedItemIndex}
                                    itemLabelFn={itemLabelFn}
                                    dropdownMenuRef={setDropdownMenuElement}
                                    isOpen={isOpen}
                                    keyboardEvents={keyboardEvents}
                                    showDropdown={showDropdown}
                                    childReferences={childReferences}
                                    themeKeys={themeKeys}
                                    theme={theme}
                                    superscriptScope={superscriptScope}
                                />,
                                dropdownElement
                            )}
                        </Fragment>
                    )}
                </DropdownInnerWrapper>
            </DropdownWrapper>
        );
    }
}

export default withTheme(Dropdown);



// WEBPACK FOOTER //
// ./src/Components/Dropdown/index.tsx